import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Drag to reorder the Calendar drawer's calendars (calendars/_drawer_layout).
//
// A row drags only within its own list - pinned calendars among themselves,
// the rest among themselves - and the whole shown order is saved in one PATCH
// (CalendarsController#reorder_calendars). Nothing redraws: the row is already
// where it was dropped. Hidden calendars are not items and never move.
//
// Its own small controller rather than nav-reorder, which is about <a> links in
// a scrolling rail with two kinds of item; here the draggable thing is an <li>
// that holds a link AND a ... menu, and neither may start a drag of its own.
// Alt + Up / Down does the same from the keyboard, on a focused row's link.
//
// It also keeps ticking a calendar from feeling frozen. A tick is a frame
// navigation that redraws the whole drawer once every ticked calendar has been
// fetched, so the box flips the moment it is clicked (#tick), the events dim
// while they load (turbo-frame[busy] in _events_rows.scss), and the drawer's
// scroll position survives the redraw - it used to jump back to the top.
let savedScroll = 0

export default class extends Controller {
    static targets = ["list", "item"]
    static values = { url: String }

    connect() {
        // Restored after layout too: the drawer may only just have been shown.
        this._restoreScroll()
        requestAnimationFrame(() => this._restoreScroll())
        // Saved on any click in the drawer (a tick, a menu action), before the
        // frame navigates - the new drawer connects before this one leaves.
        this._onPointerDown = () => this._saveScroll()
        this.element.addEventListener("pointerdown", this._onPointerDown, true)

        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return

        this._onDragOver = this._onDragOver.bind(this)
        this.element.addEventListener("dragover", this._onDragOver)
        this.element.addEventListener("drop", (e) => { if (this._dragging) e.preventDefault() })
        this.itemTargets.forEach((item) => this._enable(item))
    }

    disconnect() {
        this.element.removeEventListener("pointerdown", this._onPointerDown, true)
        if (this._onDragOver) this.element.removeEventListener("dragover", this._onDragOver)
    }

    // Flip the box now; the frame catches up with the events.
    tick(event) {
        this._saveScroll()
        const link = event.currentTarget
        const box = link.querySelector(".cal-drawer-check")
        if (!box) return

        const checked = !box.classList.contains("is-checked")
        box.classList.toggle("is-checked", checked)
        box.innerHTML = checked ? '<i class="bi bi-check-lg"></i>' : ""
        link.setAttribute("aria-checked", checked ? "true" : "false")
    }

    _scroller() {
        return this.element.closest(".cal-drawer-scroll")
    }

    _saveScroll() {
        const scroller = this._scroller()
        if (scroller) savedScroll = scroller.scrollTop
    }

    _restoreScroll() {
        const scroller = this._scroller()
        if (scroller && savedScroll) scroller.scrollTop = savedScroll
    }

    _enable(item) {
        item.setAttribute("draggable", "true")
        item.addEventListener("dragstart", (e) => {
            this._dragging = item
            this._moved = false
            item.classList.add("is-dragging")
            e.dataTransfer.effectAllowed = "move"
            try { e.dataTransfer.setData("text/plain", item.dataset.token) } catch { /* ignore */ }
        })
        item.addEventListener("dragend", () => {
            item.classList.remove("is-dragging")
            this._dragging = null
            if (this._moved) {
                // Chrome fires no click after a drag, but some browsers do, and
                // that click would tick or untick the calendar just dropped.
                this._suppressUntil = Date.now() + 300
                this._save()
            }
        })
        item.addEventListener("click", (e) => {
            if (this._suppressUntil && Date.now() < this._suppressUntil) {
                e.preventDefault()
                e.stopPropagation()
            }
        }, true)
        item.addEventListener("keydown", (e) => this._onKey(e, item))
    }

    _onDragOver(e) {
        if (!this._dragging) return
        const over = e.target.closest ? e.target.closest("[data-calendar-drawer-reorder-target~='item']") : null
        if (!over || over === this._dragging || over.parentElement !== this._dragging.parentElement) return

        e.preventDefault()
        const rect = over.getBoundingClientRect()
        const before = (e.clientY - rect.top) < rect.height / 2
        over.parentElement.insertBefore(this._dragging, before ? over : over.nextSibling)
        this._moved = true
    }

    _onKey(e, item) {
        if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return
        const sibling = e.key === "ArrowUp" ? item.previousElementSibling : item.nextElementSibling
        if (!sibling || !sibling.matches("[data-calendar-drawer-reorder-target~='item']")) return

        e.preventDefault()
        item.parentElement.insertBefore(item, e.key === "ArrowUp" ? sibling : sibling.nextSibling)
        const focusable = item.querySelector("a, button")
        if (focusable) focusable.focus()
        this._save()
    }

    _save() {
        if (!this.urlValue) return
        const order = this.listTargets.flatMap((list) =>
            Array.from(list.querySelectorAll("[data-calendar-drawer-reorder-target~='item']")).map((item) => item.dataset.token))
        fetch(this.urlValue, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
            body: JSON.stringify({ order }),
            credentials: "same-origin"
        }).catch(() => { /* the next drag or a reload shows the saved order */ })
    }
}
